import React, { useContext, useState } from 'react'

import { useParams, useSearchParams } from 'react-router-dom';

import { Box, Button, Dialog, DialogContent, CircularProgress, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, Avatar, Typography, Checkbox, Card, CardContent, Grid, Badge } from "@mui/material";
// View selector options
const VIEW_OPTIONS = [
  { value: 'new', label: 'New', description: 'New leads' },
  { value: 'my_ongoing', label: 'My Ongoing', description: 'Your active prospects' },
  { value: 'scheduled', label: 'Follow-ups Due', description: 'Scheduled follow-ups' },
  { value: 'hot_prospects', label: 'Hot Prospects', description: 'High-potential leads' },
  { value: 'cold', label: 'Cold Leads', description: 'Not priority right now' },
];

// Map view to appropriate status IDs
const getStatusIdsForView = (view) => {
  switch (view) {
    case 'my_ongoing':
      return [100]; // ongoing status
    case 'scheduled':
      return [100]; // no-reply status (leads waiting for follow-up)
    case 'hot_prospects':
      return [200]; // signed status (successful leads)
    case 'cold':
      return [400]; // rejected/unqualified status
    default:
      return []; // all statuses
  }
};
import { gql, useMutation, useQuery } from '@apollo/client';
import { LeadlistEntryTable } from '../../../components/LeadlistEntryTable';
import { LoadingButton } from '@mui/lab';

import { Flex } from '@Components';

import i18n from '../../../i18n';
import { useTranslation } from 'react-i18next';
import { Envelope } from 'phosphor-react';
import { createTeleporter } from 'react-teleporter'
import moment from 'moment';
import { VBox } from '@/components/VBox';
import { HBox } from '@/components/HBox';
import { parseBoolean, removeNull } from '@/utils';
import { SessionContext } from '@/contexts/SessionContext';
import { UsersSelect } from '@/components/UsersSelect';
import { downloadLeadListReport } from '@/actions/console';
import { Link } from 'react-router-dom';
import { LEAD_STATUS_CODES } from '@/constants';
import { useSearchParam } from '@/hooks';

export const ToolBarTeleporter = createTeleporter()

const MUTATION = gql`mutation performActionOnLeadlistEntries(
  $action: String!,
  $slugs: [String]!,
  $period: String
) {
  performActionOnLeadlistEntries(period: $period, action: $action, slugs: $slugs) {
    leadlistEntries {
      slug
    }
  }
}`

const REFRESH_DATA_IN_LEADLIST_MUTATION = gql`
  mutation refreshDataInLeadlist(
    $slug: String!
  ) {
    refreshDataInLeadlist(
      leadlistSlug: $slug
    ) {
      leadlist {
        id
      }
    }
  }
`

const QUERY = gql`query getLeadlist(
  $slug: String!,
  $q: String,
  $isBlocked: Boolean,
  $p: Int,
  $countryIds: [String],
  $uploadId: String,
  $sortBy: String,
  $statusIds: [Int!],
  $sourceTypeIds: [ID!],
  $sourceIds: [ID!],
  $campaignIds: [ID!],
  $funnelIds: [ID!],
  $userIds: [ID!],
  $hideAssignedArtists: Boolean,
  $minListeners: Int,
  $hasDSPSupport: Boolean,
  $view: String
) {
  moodler {
    users {
      imageUrl
      username
      firstName
      lastName
    }
  }
  importify {
    upload(slug: $uploadId) {
      slug
      name
      started
      completed
      canceled
      created
    }
  }
  leads {
    sources {
      id
      name
    }
    sourceTypes {
      id
      name
    }
    funnels {
      id
      name
    }
    campaigns {
      id
      slug
      name
    }
    countries {
      slug
      identifier
      name
    }
    leadlist(slug: $slug) {
      ...LeadlistFields
      update {
        id
        started
        finished
      }
      revisions {
        slug
        name
        created
      }
      hotProspects: objects(
        q: $q,
        isBlocked: $isBlocked,
        countryIds: $countryIds,
        hideAssignedArtists: $hideAssignedArtists,
        userIds: $userIds,
        funnelIds: $funnelIds,
        campaignIds: $campaignIds,
        sourceIds: $sourceIds,
        sourceTypeIds: $sourceTypeIds,
        uploadId: $uploadId,
        sortBy: $sortBy,
        statusIds: $statusIds,
        hasEditorialSupport: $hasDSPSupport,
        p: $p,
        minListeners: $minListeners,
        view: "hot_prospects"
      ) {
        total  
      }
      objects(
        q: $q,
        isBlocked: $isBlocked,
        countryIds: $countryIds,
        hideAssignedArtists: $hideAssignedArtists,
        userIds: $userIds,
        uploadId: $uploadId,
        sortBy: $sortBy,
        funnelIds: $funnelIds,
        campaignIds: $campaignIds,
        sourceIds: $sourceIds,
        sourceTypeIds: $sourceTypeIds,
        statusIds: $statusIds,
        hasEditorialSupport: $hasDSPSupport,
        p: $p,
        minListeners: $minListeners,
        view: $view
      ) {
        meta {
          numPages,
          p,
          total
        }
        entries {
          slug
          added
          lead {
            ...LeadFields
            assignees {
              user {
                id
                username
                firstName
                lastName
              }
            }
            messages {
              id
              text
              created
              status
              user {
                username
                imageUrl
              }
            }
          }
        }
      }
    }
  }
}`

export function LeadlistView({
  slug,
  showContactButton = false,
  showWho = false,
  emptyIcon = Envelope,
  emptyText = i18n.t('No leads matching the criteria found'),
  emptyHeading = i18n.t('Sorry'),
  expanded = true,
  hasRevisions = false,
  displayColumns = []
}) {
  const { t } = useTranslation()
  const { session } = useContext(SessionContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [confirmActionDialogOpen, setConfirmActionDialogOpen] = useState(false)
  const [entries, setEntries] = useState([])
  const [period, setPeriod] = useState(null)
  const handleSubmitConfirmActionDialogForm = (event) => {
    event.preventDefault()
    performActionOnLeads({
      variables: {
        action,
        slugs: checkedLeadIds,
        period
      }
    }).then(() => {
      setConfirmActionDialogOpen(false)
    })
    return false
  }

  const q = searchParams.get('q') ?? ''
  const country = searchParams.get('country') ?? ''
  const revisionId = searchParams.get('revisionId') ?? ''
  const hasDSPSupport = searchParams.get('hasDSPSupport') ?? null
  const sortBy = removeNull(searchParams.get('sortBy')) ?? null
  const statusIds = searchParams.getAll('statusId').map(s => parseInt(s))
  const campaignIds = searchParams.getAll('campaignId')
  const sourceIds = searchParams.getAll('sourceId')
  const sourceTypeIds = searchParams.getAll('sourceTypeId')
  const funnelIds = searchParams.getAll('funnelId')
  const minListeners = !isNaN(searchParams.get('minListeners')) ? parseInt(searchParams.get('minListeners')) : null

  const setMinListeners = (value) => {
    setSearchParams(
      params => {
        if (!isNaN(value)) {
          params.set('minListeners', value)
        } else {
          params.delete('minListeners')
        }
        return params
      }
    )
  }

  const handleMinListenersChange = value => {
    if (!isNaN(value)) {
      setMinListeners(parseInt(value))
    } else {
      setMinListeners(null)
    }
  }

  const p = parseInt(searchParams.get('p') ?? 0)
  const [action, setAction] = useState('')
  // View selector state
  const view = searchParams.get('view') ? searchParams.get('view') : 'my_ongoing';

  const setView = (value) => {
    setSearchParams(params => {
      params.set('view', value)
      return params
    })
  }

  // Combine statusIds from search params and view selection
  //const effectiveStatusIds = statusIds.length > 0 ? statusIds : getStatusIdsForView(view);
  const uploadId = searchParams.get('uploadId') ?? null
  const userIds = searchParams.getAll('userId')

  const countryIds = searchParams.getAll('countryId')

  const setCountryIds = (values) => {
    setSearchParams((params) => {
      params.delete('countryId')
      values.map(countryId => params.append('countryId', countryId))
      return params
    })
  }

  const setStatusIds = (values) => {
    setSearchParams((params) => {
      params.delete('statusId')
      values.map(countryId => params.append('statusId', countryId))
      return params
    })
  }

  const setUserIds = (values) => {
    setSearchParams((params) => {
      params.delete('userId')
      values.map(v => params.append('userId', v))
      return params
    })
  }

  const isBlocked = JSON.parse(searchParams.get('isBlocked'))
  const hideAssignedArtists = JSON.parse(searchParams.get('hideAssignedArtists'))
  const [downloadingReport, setDownloadingReport] = useState(false)
  const [checkedLeadIds, setCheckedLeadIds] = useState([])
  const [confirmDeleteLeadsDialogOpen, setConfirmDeleteLeadsDialogOpen] = useState(false)
  const [deleteLeadIds, setDeleteLeadIds] = useState([])
  const handleSubmitDeleteLeadsDialogForm = (event) => {
    event.preventDefault()
    performActionOnLeads({
      variables: {
        action: 'delete',
        slugs: deleteLeadIds,
      }
    }).then(() => {
      setConfirmDeleteLeadsDialogOpen(false)
    })
    return false
  }

  const handleExportLeadListClick = event => {
    setDownloadingReport(true)
    downloadLeadListReport({
      slug,
      query: {
        slug,
        q,
        p,
        minListeners,
        sortBy,
        statusIds: statusIds.map(sId => parseInt(sId)),
        view,
        isBlocked,
        uploadId,
        userIds,
        countryIds,
        hideAssignedArtists,
        hasDSPSupport: parseBoolean(hasDSPSupport)
      }
    }).then(() => {
      setDownloadingReport(false)
    }).catch(e => {
      console.error(e)
      setDownloadingReport(false)
    })
  }

  const { data, refetch, fetchMore, previousData, loading } = useQuery(
    QUERY,
    {
      variables: {
        slug,
        q,
        minListeners,
        p,
        sortBy,
        statusIds,
        view,
        isBlocked,
        sourceIds,
        sourceTypeIds,
        funnelIds,
        campaignIds,
        uploadId,
        userIds,
        countryIds,
        hideAssignedArtists,
        hasDSPSupport: parseBoolean(hasDSPSupport)
      },
      onCompleted: (data) => {
        if (entries?.length > 0) {
          setEntries(data?.leads?.leadlist?.objects?.entries ?? [])
        } else {
          setEntries([...entries, ...data?.leads?.leadlist?.objects?.entries])
        }
      }
    }
  )

  const [
    refreshDataInLeadlist,
    {
      loading: refreshing
    }
  ] = useMutation(
    REFRESH_DATA_IN_LEADLIST_MUTATION,
    {
      onCompleted: () => {
        refetch().then(() => { }).catch(e => { })
      }
    }
  )

  const [
    performActionOnLeads,
    {
      loading: performing
    }
  ] = useMutation(
    MUTATION, {
      onCompleted: () => {
        refetch().then(() => { }).catch(e => { })
      }
    }
  )

  const countries = data?.leads?.countries ?? []

  const handleNodesChecked = ({ nodes }) => {
    setCheckedLeadIds(nodes?.map(n => n?.slug))
  }

  const handleSubmitAction = (event) => {
    event.preventDefault()
    setConfirmActionDialogOpen(true)
    return false
  }

  const handleSortByChanged = ({ sortByFieldId, sortByDirection }) => {
    let sortBy = ''
    if (sortByDirection == 'desc') {
      sortBy += '-'
    }
    if (sortByFieldId?.length > 0) {
      sortBy += sortByFieldId
    } else {
      sortBy = null
    }
    setSearchParams(params => {
      if (sortBy) {
        params.set('sortBy', sortBy)
      } else {
        params.delete('sortBy')
      }
      return params
    })
  }

  const handleClearFilterClick = event => {
    event.preventDefault()
    setSearchParams(params => {
      params.delete('userId')
      params.delete('p')
      params.delete('view')
      params.delete('sortBy')
      params.delete('sourceId')
      params.delete('sourceTypeId')
      params.delete('campaignId')
      params.delete('funnelId')
      return params
    })
    return false
  }

  const leadlist = data?.leads?.leadlist
  const users = data?.moodler?.users ?? []
  const upload = data?.importify?.upload
  const campaigns = data?.leads?.campaings ?? []
  const sources = data?.leads?.sources ?? []
  const sourceTypes = data?.leads?.sourceTypes ?? []
  const funnels = data?.leads?.funnels ?? []
  console.log(leadlist?.objects)
  console.log("page", p, leadlist)
  return (
    <>
      <Flex direction="column" width="100%">
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {upload && (
            <HBox sx={{ p: 2 }}>
              <Typography>{i18n.t('showing-leads-in-upload')} <Link to={`/dashboard/upload/${upload.slug}`}>{upload.name}</Link> {upload.created && <>created on {moment(upload.created).format('YYYY-MM-DD')}</>} {upload.started && <>started on {moment(upload.started).format('YYYY-MM-DD')}</>} {upload.canceled ? <>(but canceled on {moment(upload.canceled).format('YYYY-MM-DD')})</> : ''} {upload.completed ? <>completed on {moment(upload.completed).format('YYYY-MM-DD')}</> : ''}</Typography>
            </HBox>
          )}
          <form onSubmit={handleSubmitAction}>
            <Box sx={{ padding: 1, display: 'flex', flexDirection: "row", width: "100%", alignItems: "center", gap: 1 }}>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('action')}</InputLabel>
                <Select
                  value={action}
                  onChange={(event) => {
                    setAction(event.target.value)
                  }}
                >
                  <MenuItem value={''}>{i18n.t('select-action')}</MenuItem>
                  {LEAD_STATUS_CODES.map(status => (
                    <MenuItem value={`mark_as_${status.name}`}>{i18n.t('mark-as-status', { status: status.name })}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LoadingButton type="submit" variant="contained" loading={performing} disabled={checkedLeadIds.length === 0 || action === null}>{i18n.t('do')}</LoadingButton>
              <Box sx={{ flexGrow: 1 }} />
              {console.log("session", session)}
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('assigned-to')}</InputLabel>
                <UsersSelect
                  users={users}
                  selectedUserIds={userIds}
                  onChange={(value) => setUserIds(value)}
                />
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('sort-by')}</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(event) => {
                    setSearchParams(params => {
                      params.set('sortBy', event.target.value)
                      return params
                    })
                  }}
                >
                  <MenuItem value={null}>{i18n.t('no-sort')}</MenuItem>
                  <MenuItem value={'nextInterventionDate'}>{i18n.t('next-contact-date-ascending')}</MenuItem>
                  <MenuItem value={'-nextInterventionDate'}>{i18n.t('next-contact-date-descending')}</MenuItem>
                  <MenuItem value={'lead__created'}>{i18n.t('added-descending')}</MenuItem>
                  <MenuItem value={'-lead__created'}>{i18n.t('added-ascending')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('status')}</InputLabel>
                <Select
                  multiple
                  defaultValue={statusIds}
                  renderValue={() => <Typography>{statusIds.map(sId => i18n.t(LEAD_STATUS_CODES.find(s => s.id == sId)?.name)).join(', ')}</Typography>}
                  onChange={(event) => {
                    setStatusIds(event.target.value)
                    // Reset view to default when manual status selection is made
                    if (event.target.value.length > 0) {
                      setView('my_ongoing')
                    }
                  }}
                >
                  {LEAD_STATUS_CODES.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={statusIds.find(sId => sId === s.id)} />
                      <Typography>{i18n.t(s.name)}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('campaign')}</InputLabel>
                <Select
                  multiple
                  defaultValue={campaignIds}
                  renderValue={() => <Typography>{campaignIds.map(sId => campaigns?.find(s => s.id == sId)?.name).join(', ')}</Typography>}
                  onChange={(event) => {
                    setSearchParams(params => {
                      params.delete('campaignId')
                      event.target.value.map(v => params.append('campaignId', v))
                      return params
                    })
                  }}
                >
                  {campaigns.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={campaignIds.find(sId => sId == s.id)} />
                      <Typography>{i18n.t(s.name)}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('source')}</InputLabel>
                <Select
                  multiple
                  defaultValue={sourceIds}
                  renderValue={() => <Typography>{sourceIds.map(sId => sources?.find(s => s.id == sId)?.name).join(', ')}</Typography>}
                  onChange={(event) => {
                    setSearchParams(params => {
                      params.delete('sourceId')
                      event.target.value.map(v => params.append('sourceId', v))
                      return params
                    })
                  }}
                >
                  {sources.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={sourceIds.find(sId => sId == s.id)} />
                      <Typography>{i18n.t(s.name)}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('source-type')}</InputLabel>
                <Select
                  multiple
                  defaultValue={sourceTypeIds}
                  renderValue={() => <Typography>{sourceTypeIds.map(sId => sourceTypes?.find(s => s.id == sId)?.name).join(', ')}</Typography>}
                  onChange={(event) => {
                    setSearchParams(params => {
                      params.delete('sourceTypeId')
                      event.target.value.map(v => params.append('sourceTypeId', v))
                      return params
                    })
                  }}
                >
                  <MenuItem value={null}>
                    <Typography>{i18n.t('select-source-type')}</Typography>
                  </MenuItem>
                  {sourceTypes.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={sourceTypeIds.find(sId => sId == s.id)} />
                      <Typography>{i18n.t(s.name)}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('funnel')}</InputLabel>
                <Select
                  multiple
                  defaultValue={funnelIds}
                  renderValue={() => <Typography>{funnelIds.map(sId => funnels?.find(s => s.id == sId)?.name).join(', ')}</Typography>}
                  onChange={(event) => {
                    setSearchParams(params => {
                      params.delete('funnelId')
                      event.target.value.map(value => params.append('funnelId', value))
                      return params
                    })
                  }}
                >
                  {funnels.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={funnelIds.find(sId => sId == s.id)} />
                      <Typography>{i18n.t(s.name)}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasRevisions && leadlist?.revisions instanceof Array && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>{i18n.t('revision')}</InputLabel>
                  <Select
                    value={revisionId}
                    onChange={(event) => {
                      setSearchParams(params => {
                        params.set('revisionId', event.target.value)
                        return params
                      })
                    }}
                  >
                    <MenuItem selected={!revisionId || revisionId === 'discover'} value={'discover'}>{i18n.t('Cumulative')}</MenuItem>
                    {leadlist?.revisions.map(revision => (
                      <MenuItem key={revision.id} selected={revisionId === revision.slug} value={revision.slug}>{moment(revision.created).format('YYYY-MM-DD HH:MM')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('country')}</InputLabel>
                <Select
                  defaultValue={countryIds}
                  multiple
                  onChange={(event) => {
                    setCountryIds(event.target.value)
                  }}
                  renderValue={() => <Typography>{countryIds.join(', ')}</Typography>}
                >
                  <MenuItem value={''}>{i18n.t('all-countries')}</MenuItem>
                  {countries?.map(c => (
                    <MenuItem
                      value={c.identifier}
                      key={c.identifier}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Checkbox checked={countryIds.find(cId => cId === c.identifier)} />
                        <Typography>{i18n.t(c.name)}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch aria-label={i18n.t("Hide assigned artists")} defaultValue={hideAssignedArtists} onChange={(event) => setSearchParams(params => { params.set('hideAssignedArtists', event.target.checked); return params }, { replace: true })} />
                }
                label={i18n.t('Hide assigned artists')}
              />
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{i18n.t('editorial-impact')}</InputLabel>
                <Select
                  value={hasDSPSupport}
                  onChange={(event) => {
                    setSearchParams(params => {
                      params.set('hasDSPSupport', event.target.value)
                      return params
                    })
                  }}
                >
                  <MenuItem value={null}>{i18n.t('')}</MenuItem>
                  <MenuItem value={true}>{i18n.t('leads-with-editorial-impact')}</MenuItem>
                  <MenuItem value={false}>{i18n.t('leads-without-editorial-impact')}</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch aria-label={i18n.t("Show blocked")} defaultValue={isBlocked} onChange={(event) => setSearchParams({ country, hideAssignedArtists, isBlocked: event.target.checked, q: q ?? '' }, { replace: true })} />
                }
                label={i18n.t('Show blocked')}
              />
              <LoadingButton variant="contained" onClick={handleClearFilterClick}>{i18n.t('clear-filters')}</LoadingButton>
              <LoadingButton variant="contained" loading={downloadingReport} onClick={handleExportLeadListClick}>{i18n.t('export-to-excel')}</LoadingButton>

              {/*<LoadingButton variant="contained" loading={refreshing || leadlist?.update} onClick={() => refreshDataInLeadlist({ variables: { slug } })}>{i18n.t('refresh-data')}</LoadingButton>*/}
              <TextField label={i18n.t('Search')} variant="standard" value={q} onChange={(event) => setSearchParams({ country, hideAssignedArtists, q: event.target.value ?? '', isBlocked }, { replace: true })} />
            </Box>
          </form>
        </Box>
        {/* View Selector UI */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {VIEW_OPTIONS.map((option) => (
              <Grid item xs={12} sm={6} md={3} key={option.value}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: view === option.value ? 2 : 1,
                    borderColor: view === option.value ? 'primary.main' : 'divider',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                  onClick={() => {
                    setView(option.value);
                    // Clear manual status selection when switching views
                    setSearchParams(params => {
                      params.delete('statusId');

                      params.set('p', '0'); // Reset to first page
                      return params;
                    });
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      {option.label}
                    </Typography> 
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
        {entries && (
          <LeadlistEntryTable
            sortLocal={true}
            defaultSortByFieldId={searchParams.get('sortBy')?.replace(/-/, '')}
            defaultSortByDirection={searchParams.get('sortBy')?.startsWith('-') ? 'desc' : 'asc'}
            showContactButton={showContactButton}
            onSortByChanged={handleSortByChanged}
            onMinListenersChange={handleMinListenersChange}
            showWho={showWho}
            loading={(loading || refreshing) && !previousData}
            onRefetch={() => refetch()}
            entries={entries}
            emptyIcon={emptyIcon}
            expanded={expanded}
            onNodesChecked={handleNodesChecked}
            emptyText={emptyText}
            emptyHeading={emptyHeading}
            displayColumns={displayColumns}
            onCircleBackClicked={(row) => {
              setCheckedLeadIds([row.slug])
              setAction('schedule_next_contact')
              setConfirmActionDialogOpen(true)
            }}
            onDeleteClicked={(deleteLeadIds) => {
              setAction('delete')
              setDeleteLeadIds(deleteLeadIds)
              setConfirmDeleteLeadsDialogOpen(true)
            }}
          />
        )}

        {loading ? (
          <Box
            sx={{ padding: 3, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}
          >
            <CircularProgress />
          </Box>
        ) : leadlist?.objects?.entries?.length > 0 && (
          <Box
            sx={{ padding: 3, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}
          >
            {(p < leadlist?.objects?.meta?.numPages) ?
              <Button
                variant="contained"
                onClick={() => {
                  setSearchParams(params => {
                    params.set('p', p + 1)
                    return params
                  })
                }}
              >
                {i18n.t('fetch-more')}
              </Button>
              :
              <p>{i18n.t('no-more-results')}</p>
            }
          </Box>
        )}
      </Flex>
      <Dialog open={confirmActionDialogOpen} onClose={() => setConfirmActionDialogOpen(false)}>
        <DialogTitle>{i18n.t('Are you sure you want to %s %s leads?', t(action), checkedLeadIds.length)}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitConfirmActionDialogForm}>
            <VBox>
              {(action === 'schedule_next_contact') && (
                <Select
                  onChange={event => {
                    setPeriod(event.target.value)
                  }}
                  placeholder={i18n.t('select-period')}
                  name="period"
                >
                  <MenuItem selected={!period}>{i18n.t('select-period')}</MenuItem>
                  {['6-months', '12-months', '18-months', '3-years'].map(p => (
                    <MenuItem key={p} selected={period === p} value={p}>{i18n.t(p)}</MenuItem>
                  ))}
                </Select>
              )}
            </VBox>
            <HBox>
              <LoadingButton
                loading={performing}
                variant="contained"
                color="error"
                type="submit"
              >
                {i18n.t('confirm %s', t(action))}
              </LoadingButton>
              <Button
                variant="secondary"
                onClick={() => setConfirmActionDialogOpen(false)}
              >
                {i18n.t('Cancel')}
              </Button>
            </HBox>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDeleteLeadsDialogOpen} onClose={() => setConfirmDeleteLeadsDialogOpen(false)}>
        <DialogTitle>{i18n.t('Are you sure you want to delete %s leads?', t(action), deleteLeadIds.length)}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitDeleteLeadsDialogForm}>
            <LoadingButton
              loading={performing}
              variant="contained"
              color="error"
              type="submit"
            >
              {i18n.t('Delete leads', t(action))}
            </LoadingButton>
            <Button
              variant="secondary"
              onClick={() => setConfirmDeleteLeadsDialogOpen(false)}
            >
              {i18n.t('Keep leads')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function LeadlistOverviewPage() {
  const { slug } = useParams()
  return (
    <LeadlistView slug={slug} />
  )
}

export default LeadlistOverviewPage